<template>
  <div class="help">
    <div class="page-header">
      <h2>帮助手册</h2>
      <p>系统功能说明和使用流程</p>
    </div>
    
    <div class="page-content">
      <el-row :gutter="20">
        <!-- 左侧目录 -->
        <el-col :span="6">
          <el-card>
            <div slot="header">
              <span>目录</span>
            </div>
            <el-menu
              :default-active="activeSection"
              @select="handleMenuSelect"
              class="help-menu">
              <el-menu-item index="overview">
                <i class="el-icon-info"></i>
                <span slot="title">系统概述</span>
              </el-menu-item>
              <el-menu-item index="import">
                <i class="el-icon-upload"></i>
                <span slot="title">数据导入</span>
              </el-menu-item>
              <el-menu-item index="projects">
                <i class="el-icon-office-building"></i>
                <span slot="title">项目管理</span>
              </el-menu-item>
              <el-menu-item index="packages">
                <i class="el-icon-box"></i>
                <span slot="title">包管理</span>
              </el-menu-item>
              <el-menu-item index="bid-records">
                <i class="el-icon-document"></i>
                <span slot="title">投标记录</span>
              </el-menu-item>
              <el-menu-item index="analysis">
                <i class="el-icon-s-data"></i>
                <span slot="title">投标人分析</span>
              </el-menu-item>
              <el-menu-item index="statistics">
                <i class="el-icon-data-line"></i>
                <span slot="title">数据统计</span>
              </el-menu-item>
            </el-menu>
          </el-card>
        </el-col>
        
        <!-- 右侧内容 -->
        <el-col :span="18">
          <el-card>
            <!-- 系统概述 -->
            <div v-show="activeSection === 'overview'" class="help-section">
              <h3>系统概述</h3>
              <el-divider></el-divider>
              <div class="help-content">
                <p><strong>投标管理系统</strong>是一个用于管理投标项目、包、分包和投标记录的综合性系统。</p>
                
                <h4>主要功能模块：</h4>
                <ul>
                  <li><strong>数据导入</strong>：支持Excel文件导入，自动解析并导入项目、包、分包和投标记录数据</li>
                  <li><strong>项目管理</strong>：管理项目单位信息</li>
                  <li><strong>包管理</strong>：管理主包和分包，支持编辑、删除和价格计算</li>
                  <li><strong>投标记录</strong>：查看和管理投标记录，支持树形结构展示和多条件筛选</li>
                  <li><strong>投标人分析</strong>：分析投标人的投标情况</li>
                  <li><strong>数据统计</strong>：查看系统整体数据统计</li>
                </ul>
                
                <h4>数据关系：</h4>
                <div class="data-relation">
                  <p>项目 → 主包 → 分包 → 投标记录</p>
                  <p>投标人 → 投标记录</p>
                </div>
              </div>
            </div>
            
            <!-- 数据导入 -->
            <div v-show="activeSection === 'import'" class="help-section">
              <h3>数据导入</h3>
              <el-divider></el-divider>
              <div class="help-content">
                <h4>功能说明</h4>
                <p>数据导入功能支持将Excel文件中的数据批量导入到系统中，包括项目、主包、分包和投标记录。</p>
                
                <h4>使用流程</h4>
                <ol>
                  <li>
                    <strong>打开导入页面</strong>
                    <p>点击左侧菜单"数据导入"进入导入页面</p>
                  </li>
                  <li>
                    <strong>填写主包信息</strong>
                    <ul>
                      <li>点击"上传Excel"按钮打开上传对话框</li>
                      <li>选择或输入主包名称（可从已有主包中选择，也可输入新名称）</li>
                      <li>输入百分比（如果选择已有主包，会自动填充该主包的百分比）</li>
                    </ul>
                  </li>
                  <li>
                    <strong>准备Excel文件</strong>
                    <ul>
                      <li>点击"下载模版.xlsx"下载Excel模板</li>
                      <li>按照模板格式准备数据：
                        <ul>
                          <li><strong>B1</strong>：分包名称</li>
                          <li><strong>B2</strong>：分包编号</li>
                          <li><strong>B3</strong>：项目单位</li>
                          <li><strong>B4</strong>：主包/项目名称</li>
                          <li><strong>B5</strong>：采购数量</li>
                          <li><strong>A1</strong>（可选）：主包百分比（如"80%"）</li>
                          <li><strong>第7行开始</strong>：投标数据
                            <ul>
                              <li><strong>A列</strong>：投标人名称</li>
                              <li><strong>B列</strong>：投标总价</li>
                            </ul>
                          </li>
                        </ul>
                      </li>
                      <li>每个工作表（Sheet）代表一个分包</li>
                    </ul>
                  </li>
                  <li>
                    <strong>上传文件</strong>
                    <ul>
                      <li>点击"选择文件"按钮选择准备好的Excel文件</li>
                      <li>点击"开始上传"按钮开始导入</li>
                      <li>系统会显示导入进度，包括当前步骤和详情</li>
                    </ul>
                  </li>
                  <li>
                    <strong>导入完成</strong>
                    <ul>
                      <li>导入成功后，系统会自动计算单价、合理价格、价格分等指标</li>
                      <li>可以在"包管理"和"投标记录"页面查看导入的数据</li>
                    </ul>
                  </li>
                </ol>
                
                <h4>注意事项</h4>
                <el-alert
                  title="重要提示"
                  type="warning"
                  :closable="false"
                  show-icon>
                  <ul style="margin: 10px 0; padding-left: 20px;">
                    <li>Excel文件格式必须符合模板要求</li>
                    <li>文件大小不能超过10MB</li>
                    <li>支持.xlsx和.xls格式</li>
                    <li>如果主包名称已存在，系统会使用已有主包，并更新百分比</li>
                    <li>如果分包编号已存在，系统会更新该分包的数据</li>
                  </ul>
                </el-alert>
              </div>
            </div>
            
            <!-- 项目管理 -->
            <div v-show="activeSection === 'projects'" class="help-section">
              <h3>项目管理</h3>
              <el-divider></el-divider>
              <div class="help-content">
                <h4>功能说明</h4>
                <p>项目管理用于查看和管理项目单位信息。项目是系统的基础数据，每个项目可以包含多个主包。</p>
                
                <h4>主要功能</h4>
                <ul>
                  <li><strong>查看项目列表</strong>：分页显示所有项目，支持关键词搜索</li>
                  <li><strong>搜索项目</strong>：通过项目单位名称搜索</li>
                </ul>
                
                <h4>使用流程</h4>
                <ol>
                  <li>点击左侧菜单"项目管理"进入项目列表页面</li>
                  <li>在搜索框输入关键词搜索项目（可选）</li>
                  <li>查看项目列表，包括项目ID、项目单位名称、创建时间等信息</li>
                </ol>
              </div>
            </div>
            
            <!-- 包管理 -->
            <div v-show="activeSection === 'packages'" class="help-section">
              <h3>包管理</h3>
              <el-divider></el-divider>
              <div class="help-content">
                <h4>功能说明</h4>
                <p>包管理用于管理主包和分包信息，支持树形结构展示，主包下显示所有分包。</p>
                
                <h4>主要功能</h4>
                <ul>
                  <li><strong>查看包列表</strong>：树形结构展示，主包为父节点，分包为子节点</li>
                  <li><strong>编辑主包名称</strong>：点击主包名称旁的"编辑"按钮修改主包名称</li>
                  <li><strong>编辑主包百分比</strong>：点击百分比旁的"编辑"按钮修改百分比，系统会自动重新计算所有分包的价格指标</li>
                  <li><strong>编辑分包名称和编号</strong>：点击分包名称或编号旁的"编辑"按钮修改</li>
                  <li><strong>编辑分包采购数量</strong>：点击采购数量旁的"编辑"按钮修改，系统会自动重新计算价格</li>
                  <li><strong>删除主包</strong>：删除主包会同时删除该主包下的所有分包和投标记录</li>
                  <li><strong>删除分包</strong>：删除分包会同时删除该分包下的所有投标记录</li>
                </ul>
                
                <h4>使用流程</h4>
                <ol>
                  <li>
                    <strong>查看包列表</strong>
                    <ul>
                      <li>点击左侧菜单"包管理"进入包列表页面</li>
                      <li>系统以树形结构展示，默认展开所有节点</li>
                      <li>可以使用关键词搜索（搜索主包名称、分包名称、分包编号、项目单位）</li>
                    </ul>
                  </li>
                  <li>
                    <strong>编辑主包信息</strong>
                    <ul>
                      <li>点击主包名称旁的"编辑"按钮修改主包名称</li>
                      <li>点击百分比旁的"编辑"按钮修改百分比</li>
                      <li>修改百分比后，系统会自动重新计算该主包下所有分包的价格指标和投标记录的价格分</li>
                    </ul>
                  </li>
                  <li>
                    <strong>编辑分包信息</strong>
                    <ul>
                      <li>点击分包名称旁的"编辑"按钮修改分包名称和编号</li>
                      <li>点击分包编号旁的"编辑"按钮也可以修改分包信息</li>
                      <li>点击采购数量旁的"编辑"按钮修改采购数量</li>
                      <li>修改采购数量后，系统会自动重新计算该分包的价格指标和投标记录的价格分</li>
                    </ul>
                  </li>
                  <li>
                    <strong>删除包</strong>
                    <ul>
                      <li>点击操作列的"删除"按钮</li>
                      <li>确认删除操作（删除主包会删除所有相关数据，操作不可恢复）</li>
                    </ul>
                  </li>
                </ol>
                
                <h4>价格计算说明</h4>
                <ul>
                  <li><strong>单价</strong> = 投标价格 / 采购数量 × 10000</li>
                  <li><strong>所有均价</strong> = 所有有效单价的平均值</li>
                  <li><strong>有效均价</strong> = 在合理范围内的单价平均值（所有均价的85%-105%）</li>
                  <li><strong>基准价</strong> = 有效均价 × (1 + 百分比/100)</li>
                  <li><strong>价格分</strong>：根据单价与基准价的差异计算，最高40分</li>
                </ul>
              </div>
            </div>
            
            <!-- 投标记录 -->
            <div v-show="activeSection === 'bid-records'" class="help-section">
              <h3>投标记录</h3>
              <el-divider></el-divider>
              <div class="help-content">
                <h4>功能说明</h4>
                <p>投标记录用于查看和管理所有投标记录，支持树形结构展示和多条件筛选。</p>
                
                <h4>主要功能</h4>
                <ul>
                  <li><strong>树形结构展示</strong>：主包 → 分包 → 投标记录的三级结构</li>
                  <li><strong>多条件筛选</strong>：支持按项目、主包/分包、投标人筛选</li>
                  <li><strong>多选支持</strong>：可以选择一个或多个主包，也可以选择多个分包</li>
                  <li><strong>排序功能</strong>：支持按序号、投标价格、单价、价格分、创建时间排序</li>
                  <li><strong>导出Excel</strong>：导出为Excel格式，每个主包一个Sheet，分包分组展示</li>
                </ul>
                
                <h4>使用流程</h4>
                <ol>
                  <li>
                    <strong>查看投标记录</strong>
                    <ul>
                      <li>点击左侧菜单"投标记录"进入投标记录页面</li>
                      <li>系统以树形结构展示，默认展开所有节点</li>
                      <li>主包显示主包名称</li>
                      <li>分包显示分包名称、编号、采购数量、所有均价、有效均价、基准价</li>
                      <li>投标记录显示序号、投标人、投标价格、单价、合理价格、价格分、创建时间</li>
                    </ul>
                  </li>
                  <li>
                    <strong>筛选投标记录</strong>
                    <ul>
                      <li>点击"选择项目"输入框，在弹出的对话框中选择项目</li>
                      <li>点击"选择包"输入框，在弹出的对话框中选择主包或分包（支持多选）</li>
                      <li>点击"选择投标人"输入框，在弹出的对话框中选择投标人</li>
                      <li>点击"查询"按钮应用筛选条件</li>
                      <li>点击"清空筛选"按钮清除所有筛选条件</li>
                    </ul>
                  </li>
                  <li>
                    <strong>排序</strong>
                    <ul>
                      <li>点击表头的排序图标（序号、投标价格、单价、价格分）进行排序</li>
                      <li>支持升序和降序</li>
                    </ul>
                  </li>
                  <li>
                    <strong>导出Excel</strong>
                    <ul>
                      <li>设置筛选条件（可选）</li>
                      <li>点击"导出Excel"按钮</li>
                      <li>系统会生成Excel文件，每个主包一个Sheet</li>
                      <li>每个Sheet中，分包信息显示在分组标题行，投标记录显示在数据行</li>
                    </ul>
                  </li>
                </ol>
                
                <h4>选择包说明</h4>
                <ul>
                  <li>可以选择一个或多个主包</li>
                  <li>可以选择多个分包</li>
                  <li>选择主包时，会自动选中该主包下的所有分包</li>
                  <li>如果选择了主包，查询时会返回该主包下的所有投标记录</li>
                  <li>如果选择了分包，查询时会返回这些分包的投标记录</li>
                </ul>
              </div>
            </div>
            
            <!-- 投标人分析 -->
            <div v-show="activeSection === 'analysis'" class="help-section">
              <h3>投标人分析</h3>
              <el-divider></el-divider>
              <div class="help-content">
                <h4>功能说明</h4>
                <p>投标人分析用于分析投标人的投标情况，包括投标次数、平均价格分等信息。</p>
                
                <h4>主要功能</h4>
                <ul>
                  <li><strong>查看投标人列表</strong>：显示所有投标人及其投标统计信息</li>
                  <li><strong>查看投标详情</strong>：查看某个投标人的所有投标记录</li>
                  <li><strong>导出分析报告</strong>：导出投标人分析数据为Excel</li>
                </ul>
                
                <h4>使用流程</h4>
                <ol>
                  <li>点击左侧菜单"投标人分析"进入分析页面</li>
                  <li>查看投标人列表，包括投标次数、平均价格分等信息</li>
                  <li>点击某个投标人可以查看其详细投标记录</li>
                  <li>点击"导出"按钮可以导出分析报告</li>
                </ol>
              </div>
            </div>
            
            <!-- 数据统计 -->
            <div v-show="activeSection === 'statistics'" class="help-section">
              <h3>数据统计</h3>
              <el-divider></el-divider>
              <div class="help-content">
                <h4>功能说明</h4>
                <p>数据统计页面显示系统的整体数据统计信息，包括项目数、主包数、投标人数、投标记录数等。</p>
                
                <h4>主要功能</h4>
                <ul>
                  <li><strong>查看统计数据</strong>：显示各模块的数据统计</li>
                  <li><strong>快速导航</strong>：点击统计卡片可以快速跳转到对应模块</li>
                </ul>
                
                <h4>使用流程</h4>
                <ol>
                  <li>点击左侧菜单"数据统计"进入统计页面</li>
                  <li>查看各项统计数据</li>
                  <li>点击统计卡片可以跳转到对应的管理页面</li>
                </ol>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Help',
  data() {
    return {
      activeSection: 'overview'
    }
  },
  methods: {
    handleMenuSelect(index) {
      this.activeSection = index
    }
  }
}
</script>

<style scoped>
.help {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 10px 0;
  color: #303133;
}

.page-header p {
  margin: 0;
  color: #909399;
}

.help-menu {
  border-right: none;
}

.help-section {
  min-height: 500px;
}

.help-section h3 {
  color: #303133;
  margin-top: 0;
}

.help-section h4 {
  color: #606266;
  margin-top: 20px;
  margin-bottom: 10px;
}

.help-content {
  line-height: 1.8;
  color: #606266;
}

.help-content p {
  margin: 10px 0;
}

.help-content ul,
.help-content ol {
  margin: 10px 0;
  padding-left: 30px;
}

.help-content li {
  margin: 8px 0;
}

.help-content li p {
  margin: 5px 0;
}

.data-relation {
  background-color: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  margin: 15px 0;
  border-left: 4px solid #409eff;
}

.data-relation p {
  margin: 5px 0;
  font-family: monospace;
  font-size: 14px;
}
</style>

